import { Box, Button, Chip, Divider, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { IFieldDefinition, IWorkflowBaseRecord, IWorkflowNodeConfig, WorkflowEditorMode } from "../interfaces";
import { useEffect, useMemo, useState } from "react";
import { usePostMutation } from "../hooks/useSectorWorkflow.mutation";
import { usePatchMutation } from "../hooks/useSectorWorkflowUpdate.mutation";
import { useDeleteWithBodyMutation } from "../hooks/useSectorWorkflowDeleteItem.mutation";
import { useCatalogRecords, useWorkflowQuery } from "../hooks/useSectorWorkflow.query";
import { IPaginatedApiResponse } from "@components/common/DataSelect/dataselector.interface";
import { CheckCircleOutline, LinkOffOutlined, SearchOutlined } from "@mui/icons-material";
import { RecordCard } from "./RecordCard";

interface IRelationshipManagerProps {
  node: IWorkflowNodeConfig;
  pageSize?: number;
  onSaved?: () => Promise<void> | void;
}

enum ERelationshipItemStatus {
  Linked = "linked",
  Available = "available",
  NeedsConfig = "needs_config",
}

enum ERelationshipPanelMode {
  Create = "create",
  Edit = "edit",
}

interface IRelationshipPanelState {
  mode: ERelationshipPanelMode;
  relatedRecord: IWorkflowBaseRecord;
  relationRecord: IWorkflowBaseRecord | null;
}

interface IRelationshipFormState {
  [key: string]: any;
}

interface IRelationshipCreatePayload {
  [key: string]: unknown;
}

interface IRelationshipUpdatePayload {
  [key: string]: unknown;
}

interface IRelationshipDeletePayload {
  uid: string;
}

const getRecordLabel = (record: IWorkflowBaseRecord | null): string => {
  if (!record) {
    return "";
  }

  return record.name ?? record?.range ?? record.uid;
};

const getFieldKey = (field: IFieldDefinition<any>): string => {
  return String(field.key);
};

const getInitialFieldValue = (field: IFieldDefinition<any>, record?: IWorkflowBaseRecord | null): unknown => {
  const fieldKey = getFieldKey(field);
  const recordMap = record as unknown as Record<string, unknown> | null;

  if (recordMap && recordMap[fieldKey] !== undefined) {
    return recordMap[fieldKey];
  }

  if (field.type === "object") {
    return field.fields.reduce<IRelationshipFormState>((accumulator, nestedField) => {
      accumulator[getFieldKey(nestedField)] = getInitialFieldValue(nestedField);
      return accumulator;
    }, {});
  }

  if (field.type === "array") {
    return [];
  }

  if (field.type === "number") {
    return "";
  }

  return "";
};

const buildInitialRelationshipFormState = (fields: IFieldDefinition<any>[], relationRecord?: IWorkflowBaseRecord | null): IRelationshipFormState => {
  return fields.reduce<IRelationshipFormState>((accumulator, field) => {
    accumulator[getFieldKey(field)] = getInitialFieldValue(field, relationRecord);
    return accumulator;
  }, {});
};

const hasRequiredFieldValues = (fields: IFieldDefinition<any>[], values: IRelationshipFormState): boolean => {
  return fields.every((field) => {
    const fieldKey = getFieldKey(field);
    const value = values[fieldKey];

    if (field.type === "object") {
      return hasRequiredFieldValues(field.fields, value ?? {});
    }

    if (!field.required) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== undefined && value !== null && String(value).trim() !== "";
  });
};

const RelationshipExtraFieldsForm = (props: {
  fields: IFieldDefinition<any>[];
  values: IRelationshipFormState;
  disabled?: boolean;
  onChange: (values: IRelationshipFormState) => void;
}) => {
  const { fields, values, disabled = false, onChange } = props;

  const handleValueChange = (key: string, value: unknown) => {
    onChange({
      ...values,
      [key]: value,
    });
  };

  const renderField = (field: IFieldDefinition<any>) => {
    const fieldKey = getFieldKey(field);
    const value = values[fieldKey];

    if (field.type === "object") {
      const objectValues = (value ?? {}) as IRelationshipFormState;

      return (
        <Paper key={fieldKey} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {field.label}
          </Typography>

          <RelationshipExtraFieldsForm
            fields={field.fields}
            values={objectValues}
            disabled={disabled}
            onChange={(nextObjectValues) => {
              handleValueChange(fieldKey, nextObjectValues);
            }}
          />
        </Paper>
      );
    }

    if (field.type === "array") {
      return (
        <TextField
          key={fieldKey}
          size="small"
          fullWidth
          disabled={disabled || field.disabled}
          required={field.required}
          label={field.label}
          placeholder={field.placeholder ?? "Enter values separated by commas"}
          value={Array.isArray(value) ? value.join(", ") : ""}
          onChange={(event) => {
            const nextValue = event.target.value
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);

            handleValueChange(fieldKey, nextValue);
          }}
        />
      );
    }

    if (field.type === "select") {
      return (
        <TextField
          key={fieldKey}
          size="small"
          fullWidth
          select
          disabled={disabled || field.disabled}
          required={field.required}
          label={field.label}
          value={value ?? ""}
          onChange={(event) => handleValueChange(fieldKey, event.target.value)}
        >
          {field.options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === "relation") {
      const source = field.relation.source;

      if ("options" in source) {
        return (
          <TextField
            key={fieldKey}
            size="small"
            fullWidth
            select
            disabled={disabled || field.disabled}
            required={field.required}
            label={field.label}
            value={value ?? ""}
            onChange={(event) => handleValueChange(fieldKey, event.target.value)}
          >
            {source.options.map((option) => (
              <MenuItem key={option.uid} value={option.uid}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      }

      return (
        <TextField
          key={fieldKey}
          size="small"
          fullWidth
          disabled={disabled || field.disabled}
          required={field.required}
          label={field.label}
          placeholder={field.placeholder ?? "Enter related UID"}
          value={value ?? ""}
          onChange={(event) => handleValueChange(fieldKey, event.target.value)}
        />
      );
    }

    return (
      <TextField
        key={fieldKey}
        size="small"
        fullWidth
        disabled={disabled || field.disabled}
        required={field.required}
        type={field.type === "number" ? "number" : "text"}
        label={field.label}
        placeholder={field.placeholder}
        value={value ?? ""}
        onChange={(event) => {
          const nextValue = field.type === "number" && event.target.value !== "" ? Number(event.target.value) : event.target.value;
          handleValueChange(fieldKey, nextValue);
        }}
      />
    );
  };

  return <Stack spacing={2}>{fields.map(renderField)}</Stack>;
};

export const RelationshipManager = (props: IRelationshipManagerProps) => {
  const { node, pageSize = 20, onSaved } = props;

  const relationshipConfig = node.relationshipConfig;

  const [baseSearch, setBaseSearch] = useState("");
  const [relatedSearch, setRelatedSearch] = useState("");
  const [basePage, setBasePage] = useState(1);
  const [relatedPage, setRelatedPage] = useState(1);
  const [selectedBaseUid, setSelectedBaseUid] = useState<string | null>(null);
  const [panelState, setPanelState] = useState<IRelationshipPanelState | null>(null);
  const [formValues, setFormValues] = useState<IRelationshipFormState>({});

  const createMutation = usePostMutation<IWorkflowBaseRecord, IRelationshipCreatePayload>({});
  const updateMutation = usePatchMutation<IWorkflowBaseRecord, IRelationshipUpdatePayload>({});
  const deleteMutation = useDeleteWithBodyMutation<void, IRelationshipDeletePayload>({});

  const baseNode = useMemo<IWorkflowNodeConfig | null>(() => {
    if (!relationshipConfig) {
      return null;
    }

    return {
      key: `${node.key}-base`,
      title: relationshipConfig.base.pluralLabel,
      subtitle: "",
      group: node.group,
      mode: WorkflowEditorMode.Catalog,
      requestConfig: relationshipConfig.base.requestConfig,
      fields: [],
    };
  }, [node.group, node.key, relationshipConfig]);

  const relatedNode = useMemo<IWorkflowNodeConfig | null>(() => {
    if (!relationshipConfig) {
      return null;
    }

    return {
      key: `${node.key}-related`,
      title: relationshipConfig.related.pluralLabel,
      subtitle: "",
      group: node.group,
      mode: WorkflowEditorMode.Catalog,
      requestConfig: relationshipConfig.related.requestConfig,
      fields: [],
    };
  }, [node.group, node.key, relationshipConfig]);

  const relationFields = node.fields ?? [];

  const extraFields = useMemo(() => {
    if (!relationshipConfig) {
      return [];
    }

    return relationFields.filter((field) => {
      const fieldKey = getFieldKey(field);

      return fieldKey !== relationshipConfig.base.fieldKey && fieldKey !== relationshipConfig.related.fieldKey;
    });
  }, [relationshipConfig, relationFields]);

  const hasExtraFields = extraFields.length > 0;

  const {
    records: baseRecords,
    page: currentBasePage,
    totalPages: totalBasePages,
    isLoading: isLoadingBaseRecords,
    isFetching: isFetchingBaseRecords,
  } = useCatalogRecords({
    selectedNode: baseNode ?? node,
    queryParams: {
      page: basePage,
      pageSize,
      [relationshipConfig?.base.searchParamKey ?? "name"]: baseSearch || undefined,
    },
    enabled: Boolean(baseNode && relationshipConfig),
  });

  const {
    records: relatedRecords,
    page: currentRelatedPage,
    totalPages: totalRelatedPages,
    isLoading: isLoadingRelatedRecords,
    isFetching: isFetchingRelatedRecords,
  } = useCatalogRecords({
    selectedNode: relatedNode ?? node,
    queryParams: {
      page: relatedPage,
      pageSize,
      [relationshipConfig?.related.searchParamKey ?? "name"]: relatedSearch || undefined,
    },
    enabled: Boolean(relatedNode && relationshipConfig && selectedBaseUid),
  });

  const relationRecordsQuery = useWorkflowQuery<IPaginatedApiResponse<IWorkflowBaseRecord>>({
    queryKey: [
      "sector-workflows",
      "relationship-builder-records",
      node.requestConfig?.uri ?? "",
      relationshipConfig?.base.fieldKey ?? "",
      selectedBaseUid ?? "",
    ],
    uri: node.requestConfig?.uri ?? "",
    queryParams: {
      page: 1,
      pageSize: 500,
      [relationshipConfig?.base.fieldKey ?? ""]: selectedBaseUid ?? undefined,
    },
    enabled: Boolean(node.requestConfig?.uri && relationshipConfig && selectedBaseUid),
  });

  const relationRecords = relationRecordsQuery.data?.items ?? [];
  const selectedBaseRecord = useMemo(() => {
    return baseRecords.find((record) => record.uid === selectedBaseUid) ?? null;
  }, [baseRecords, selectedBaseUid]);

  const relationRecordByRelatedUid = useMemo(() => {
    if (!relationshipConfig) {
      return new Map<string, IWorkflowBaseRecord>();
    }

    return relationRecords.reduce<Map<string, IWorkflowBaseRecord>>((accumulator, relationRecord) => {
      const relationMap = relationRecord as unknown as Record<string, unknown>;
      const relatedUid = relationMap[relationshipConfig.related.fieldKey];

      if (typeof relatedUid === "string") {
        accumulator.set(relatedUid, relationRecord);
      }

      return accumulator;
    }, new Map<string, IWorkflowBaseRecord>());
  }, [relationshipConfig, relationRecords]);

  useEffect(() => {
    setBasePage(1);
  }, [baseSearch, node.key]);

  useEffect(() => {
    setRelatedPage(1);
  }, [relatedSearch, selectedBaseUid, node.key]);

  useEffect(() => {
    setPanelState(null);
    setFormValues({});
  }, [selectedBaseUid]);

  const getRelatedStatus = (relatedRecord: IWorkflowBaseRecord): ERelationshipItemStatus => {
    const isLinked = relationRecordByRelatedUid.has(relatedRecord.uid);

    if (isLinked) {
      return ERelationshipItemStatus.Linked;
    }

    if (hasExtraFields) {
      return ERelationshipItemStatus.NeedsConfig;
    }

    return ERelationshipItemStatus.Available;
  };

  const openCreatePanel = (relatedRecord: IWorkflowBaseRecord) => {
    setPanelState({
      mode: ERelationshipPanelMode.Create,
      relatedRecord,
      relationRecord: null,
    });

    setFormValues(buildInitialRelationshipFormState(extraFields));
  };

  const openEditPanel = (relatedRecord: IWorkflowBaseRecord, relationRecord: IWorkflowBaseRecord) => {
    setPanelState({
      mode: ERelationshipPanelMode.Edit,
      relatedRecord,
      relationRecord,
    });

    setFormValues(buildInitialRelationshipFormState(extraFields, relationRecord));
  };

  const buildSavePayload = (): IRelationshipCreatePayload => {
    if (!relationshipConfig || !selectedBaseUid || !panelState) {
      return {};
    }

    return {
      [relationshipConfig.base.fieldKey]: selectedBaseUid,
      [relationshipConfig.related.fieldKey]: panelState.relatedRecord.uid,
      ...formValues,
    };
  };

  const handleCreateRelation = async (relatedRecord: IWorkflowBaseRecord) => {
    if (!relationshipConfig || !selectedBaseUid || !node.requestConfig?.uri) {
      return;
    }

    if (relationRecordByRelatedUid.has(relatedRecord.uid)) {
      return;
    }

    const uri = relationshipConfig.saveConfig?.createUri ?? node.requestConfig.uri;

    await createMutation.mutateAsync({
      uri,
      body: {
        [relationshipConfig.base.fieldKey]: selectedBaseUid,
        [relationshipConfig.related.fieldKey]: relatedRecord.uid,
      },
    });

    await relationRecordsQuery.refetch();
    await onSaved?.();
  };

  const handleRelatedRecordClick = async (relatedRecord: IWorkflowBaseRecord) => {
    const linkedRelationRecord = relationRecordByRelatedUid.get(relatedRecord.uid);

    if (linkedRelationRecord) {
      openEditPanel(relatedRecord, linkedRelationRecord);
      return;
    }

    openCreatePanel(relatedRecord);
    return;
  };

  const handleSavePanel = async () => {
    if (!relationshipConfig || !node.requestConfig?.uri || !panelState) {
      return;
    }

    if (!hasRequiredFieldValues(extraFields, formValues)) {
      return;
    }

    const payload = buildSavePayload();

    if (panelState.mode === ERelationshipPanelMode.Create) {
      const uri = relationshipConfig.saveConfig?.createUri ?? node.requestConfig.uri;

      await createMutation.mutateAsync({
        uri,
        body: payload,
      });
    }

    if (panelState.mode === ERelationshipPanelMode.Edit && panelState.relationRecord) {
      const uri = relationshipConfig.saveConfig?.updateUri ?? node.requestConfig.uri;

      await updateMutation.mutateAsync({
        uri: `${uri}/${panelState.relationRecord.uid}`,
        body: payload,
      });
    }

    setPanelState(null);
    setFormValues({});

    await relationRecordsQuery.refetch();
    await onSaved?.();
  };

  const handleUnlink = async (event: React.MouseEvent<HTMLButtonElement>, relatedRecord: IWorkflowBaseRecord) => {
    event.stopPropagation();

    if (!relationshipConfig || !node.requestConfig?.uri) {
      return;
    }

    const relationRecord = relationRecordByRelatedUid.get(relatedRecord.uid);

    if (!relationRecord) {
      return;
    }

    const uri = relationshipConfig.saveConfig?.deleteUri ?? node.requestConfig.uri;

    await deleteMutation.mutateAsync({
      uri: `${uri}/${relationRecord.uid}`,
    });

    if (panelState?.relationRecord?.uid === relationRecord.uid) {
      setPanelState(null);
      setFormValues({});
    }

    await relationRecordsQuery.refetch();
    await onSaved?.();
  };

  const handleBaseScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

    if (isNearBottom && currentBasePage < totalBasePages && !isFetchingBaseRecords) {
      setBasePage((current) => current + 1);
    }
  };

  const handleRelatedScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

    if (isNearBottom && currentRelatedPage < totalRelatedPages && !isFetchingRelatedRecords) {
      setRelatedPage((current) => current + 1);
    }
  };

  if (!relationshipConfig) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="body2" color="error">
          Missing relationshipConfig for this relationship node.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "340px minmax(360px, 1fr) 420px",
        gap: 2,
        height: "100%",
        minHeight: 0,
      }}
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" fontWeight={700}>
          Select {relationshipConfig.base.label}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose one base record
        </Typography>

        <TextField
          size="small"
          fullWidth
          value={baseSearch}
          placeholder={`Search ${relationshipConfig.base.pluralLabel.toLowerCase()}...`}
          onChange={(event) => setBaseSearch(event.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box onScroll={handleBaseScroll} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {isLoadingBaseRecords ? (
            <Typography variant="body2" color="text.secondary">
              Loading records...
            </Typography>
          ) : null}

          {!isLoadingBaseRecords ? (
            <Stack spacing={1.25}>
              {baseRecords.map((record) => {
                const selected = selectedBaseUid === record.uid;

                return (
                  <RecordCard
                    key={record.uid}
                    title={getRecordLabel(record)}
                    //subtitle={record.description}
                    imageUrl={record.imageUrl}
                    record={record}
                    selected={selected}
                    onClick={() => setSelectedBaseUid(record.uid)}
                    trailing={selected ? <CheckCircleOutline fontSize="small" color="primary" /> : null}
                    compact={true}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" fontWeight={700}>
          Select {relationshipConfig.related.pluralLabel}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Linked records can be edited or unlinked. Available records can be linked.
        </Typography>

        <TextField
          size="small"
          fullWidth
          disabled={!selectedBaseUid}
          value={relatedSearch}
          placeholder={`Search ${relationshipConfig.related.pluralLabel.toLowerCase()}...`}
          onChange={(event) => setRelatedSearch(event.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {!selectedBaseUid ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Select one {relationshipConfig.base.label.toLowerCase()} first.
            </Typography>
          </Box>
        ) : null}

        {selectedBaseUid ? (
          <Box onScroll={handleRelatedScroll} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
            {isLoadingRelatedRecords || relationRecordsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading records...
              </Typography>
            ) : null}

            {!isLoadingRelatedRecords && !relationRecordsQuery.isLoading ? (
              <Stack spacing={1.25}>
                {relatedRecords.map((record) => {
                  const status = getRelatedStatus(record);
                  const relationRecord = relationRecordByRelatedUid.get(record.uid);
                  const isSelectedPanelRecord = panelState?.relatedRecord.uid === record.uid;

                  return (
                    <Box key={record.uid} onClick={() => void handleRelatedRecordClick(record)} sx={{ cursor: "pointer" }}>
                      <RecordCard
                        title={getRecordLabel(record)}
                        subtitle={record.description}
                        imageUrl={record.imageUrl}
                        record={record}
                        selected={isSelectedPanelRecord}
                        compact
                        trailing={
                          <Stack direction="row" spacing={1} alignItems="center">
                            {status === ERelationshipItemStatus.Linked ? (
                              <Chip size="small" label="Linked" color="success" variant="outlined" />
                            ) : null}

                            {status === ERelationshipItemStatus.Available ? <Chip size="small" label="Available" variant="outlined" /> : null}

                            {status === ERelationshipItemStatus.NeedsConfig ? (
                              <Chip size="small" label="Config" color="warning" variant="outlined" />
                            ) : null}

                            {relationRecord ? (
                              <IconButton size="small" onClick={(event) => void handleUnlink(event, record)}>
                                <LinkOffOutlined fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Stack>
                        }
                      />
                    </Box>
                  );
                })}
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" fontWeight={700}>
          Relationship Configuration
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create or update additional relationship data.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Selected {relationshipConfig.base.label}
          </Typography>

          <Typography variant="subtitle1" fontWeight={700}>
            {selectedBaseRecord ? getRecordLabel(selectedBaseRecord) : "None"}
          </Typography>

          {panelState ? (
            <>
              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" color="text.secondary">
                Selected {relationshipConfig.related.label}
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                {getRecordLabel(panelState.relatedRecord)}
              </Typography>
            </>
          ) : null}
        </Paper>

        {!panelState ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Select a linked record to edit, or select an available record to create a relationship.
            </Typography>
          </Box>
        ) : null}

        {panelState ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip
                size="small"
                color={panelState.mode === ERelationshipPanelMode.Edit ? "success" : "primary"}
                label={panelState.mode === ERelationshipPanelMode.Edit ? "Editing relation" : "New relation"}
              />
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
              {extraFields.length > 0 ? (
                <RelationshipExtraFieldsForm
                  fields={extraFields}
                  values={formValues}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  onChange={setFormValues}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This relationship has no extra configuration.
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={() => {
                  setPanelState(null);
                  setFormValues({});
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                disabled={createMutation.isPending || updateMutation.isPending || !hasRequiredFieldValues(extraFields, formValues)}
                onClick={() => void handleSavePanel()}
              >
                {panelState.mode === ERelationshipPanelMode.Edit ? "Save Changes" : "Create Relation"}
              </Button>
            </Stack>
          </>
        ) : null}
      </Paper>
    </Box>
  );
};
